import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";

import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import PublicLayout from "@/components/layout/PublicLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import RequestQuote from "@/pages/RequestQuote";
import ThankYou from "@/pages/ThankYou";
import { SystemsIndex, SystemDetail, CompareSystems } from "@/pages/Systems";
import { ServicesIndex, ServiceDetail } from "@/pages/Services";
import { IndustriesIndex, IndustryDetail } from "@/pages/Industries";
import { ServiceAreasIndex, StateDetail, CityDetail } from "@/pages/ServiceAreas";
import { FAQPage, ResourcesIndex, ArticleDetail, CaseStudiesIndex, CaseStudyDetail } from "@/pages/Resources";
import Login from "@/pages/Login";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminSettings from "@/pages/admin/AdminSettings";
import { AdminSystems, AdminIndustries, AdminServices, AdminCities, AdminArticles, AdminFaqs } from "@/pages/admin/AdminContent";

const Public = ({ children }) => <PublicLayout>{children}</PublicLayout>;

function NotFound() {
  return (
    <div className="container-x py-24 max-w-3xl">
      <div className="eyebrow text-solar mb-3">404</div>
      <h1 className="mb-4">Page not found.</h1>
      <p className="text-white/70 mb-6">The page you're looking for may have moved or not been published yet.</p>
      <a href="/" className="btn-solar inline-block">Back to Home</a>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Public><Home /></Public>} />
            <Route path="/about" element={<Public><About /></Public>} />
            <Route path="/contact" element={<Public><Contact /></Public>} />
            <Route path="/request-a-quote" element={<Public><RequestQuote /></Public>} />
            <Route path="/thank-you" element={<Public><ThankYou /></Public>} />

            <Route path="/systems" element={<Public><SystemsIndex /></Public>} />
            <Route path="/systems/compare" element={<Public><CompareSystems /></Public>} />
            <Route path="/systems/:slug" element={<Public><SystemDetail /></Public>} />

            <Route path="/services" element={<Public><ServicesIndex /></Public>} />
            <Route path="/services/:slug" element={<Public><ServiceDetail /></Public>} />

            <Route path="/industries" element={<Public><IndustriesIndex /></Public>} />
            <Route path="/industries/:slug" element={<Public><IndustryDetail /></Public>} />

            <Route path="/service-areas" element={<Public><ServiceAreasIndex /></Public>} />
            <Route path="/service-areas/:state" element={<Public><StateDetail /></Public>} />
            <Route path="/service-areas/:state/:city" element={<Public><CityDetail /></Public>} />

            <Route path="/resources" element={<Public><ResourcesIndex /></Public>} />
            <Route path="/resources/:slug" element={<Public><ArticleDetail /></Public>} />
            <Route path="/case-studies" element={<Public><CaseStudiesIndex /></Public>} />
            <Route path="/case-studies/:slug" element={<Public><CaseStudyDetail /></Public>} />
            <Route path="/faq" element={<Public><FAQPage /></Public>} />

            {/* Auth */}
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />

            {/* Admin */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminOverview />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="systems" element={<AdminSystems />} />
              <Route path="industries" element={<AdminIndustries />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="cities" element={<AdminCities />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="faqs" element={<AdminFaqs />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Public><NotFound /></Public>} />
          </Routes>
          <Toaster richColors position="top-right" theme="dark" />
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
